import React, { useEffect, useMemo, useState } from 'react';
import './AuctionsPage.css';
import TopBar from '../components/TopBar';
import AuctionCard from '../components/AuctionCard';
import Tooltip from '../components/Tooltip';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  createAuction,
  deleteAuction,
  updateAuction,
  getUserAuctions,
  getUserWatchedAuctions,
  backfillAllPmaStats,
  getPublicAuctions,
  addWatchedAuction,
  removeWatchedAuction,
  syncPublicAuction,
  removePublicAuction,
  DEFAULT_ROSTER_LIMITS,
  normalizeRosterLimits,
  normalizeRoleOverridesInput,
  getAuctionRolesMode,
  getAuctionMantraMovementLimit,
} from '../firebase/auctionService';
import { DEFAULT_MANTRA_ROLE_CONFIGURATION } from '../components/auction/MantraRoleConfiguration';
import { FANTAPP_ROLE_OVERRIDES_CONFIGURATION } from '../components/auction/FantAppRoleConfiguration';
import { containsBlasphemy } from '../utils/blasphemyFilter';
import {
  Plus,
  Sparkles,
  Loader2,
  AlertTriangle,
  X,
  Lock,
  Globe2,
  HatGlasses,
  ArrowLeftRight,
  Wand2,
  Info,
  Layers,
  Gavel,
  ListOrdered,
  Search,
  Eye,
  EyeOff,
  Users,
  Coins,
  NotebookPen,
} from 'lucide-react';

const SOURCE_OPTIONS = [
  {
    value: 'FantApp',
    label: 'FantApp!',
    description: 'Sorgente predefinita',
    badge: 'Default',
  },
  {
    value: 'FantaMaster',
    label: 'FantaMaster',
    description: 'Sorgente alternativa',
    badge: 'Alt',
  },
];

const ROLE_SYSTEM_OPTIONS = [
  {
    value: 'classic',
    label: 'Classic',
    description: 'Ruoli classic come te li immagini!',
    badge: 'Default',
  },
  {
    value: 'mantra',
    label: 'Mantra',
    description: 'Ruoli alternativi per un gioco diverso!',
    badge: 'Custom',
  },
];

const ROLE_FULL_LABELS = {
  P: 'Portieri',
  D: 'Difensori',
  C: 'Centrocampisti',
  A: 'Attaccanti',
  M: 'Giocatori di movimento',
};

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function countFilledTeams(teamNames = []) {
  return teamNames.filter((name) => String(name || '').trim()).length;
}

function getAuctionRoleSystem(auction = {}) {
  return normalizeText(auction?.rolesMode || auction?.roleSystem || auction?.roles_mode || '');
}

function getAuctionSource(auction = {}) {
  return normalizeText(auction?.playersSource || auction?.source || auction?.players_source || '');
}

function RoleLimitsCard({ role, minValue, maxValue, onMinChange, onMaxChange }) {
  return (
    <div className={`role-limit-card role-limit-card--${role}`}>
      <div className="role-limit-card__head">
        <span className={`role-limit-card__swatch role-limit-card__swatch--${role}`} aria-hidden="true" />
        <strong>{ROLE_FULL_LABELS[role] || role}</strong>
      </div>

      <div className="role-limit-card__fields">
        <label className="role-limit-card__field">
          <span>Min</span>
          <input
            className="auction-number-input"
            type="number"
            min="0"
            max={maxValue}
            step="1"
            value={minValue}
            onChange={(e) => onMinChange(Number(e.target.value))}
          />
        </label>

        <label className="role-limit-card__field">
          <span>Max</span>
          <input
            className="auction-number-input"
            type="number"
            min="1"
            step="1"
            value={maxValue}
            onChange={(e) => onMaxChange(Number(e.target.value))}
            required
          />
        </label>
      </div>
    </div>
  );
}

function ChoiceCard({ icon: Icon, badge, title, description, active, onClick, disabled = false }) {
  return (
    <button
      type="button"
      className={`choice-card ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={() => {
        if (!disabled) onClick();
      }}
      disabled={disabled}
    >
      <div className="choice-card__top">
        <div className="choice-card__icon">
          <Icon size={16} />
        </div>
        <div className="choice-card__badge">{badge}</div>
      </div>

      <div className="choice-card__body">
        <strong>{title}</strong>
        <span>{description}</span>
      </div>
    </button>
  );
}

function VisibilityToggle({ value, onChange }) {
  return (
    <div className="auction-choice-grid auction-choice-grid--visibility">
      <ChoiceCard
        icon={Lock}
        badge="Default"
        title="Privata"
        description="Visibile solo al tuo account"
        active={value === 'private'}
        onClick={() => onChange('private')}
      />

      <ChoiceCard
        icon={HatGlasses}
        badge="New"
        title="Pubblica"
        description="Visibile a tutti gli utenti"
        active={value === 'public'}
        onClick={() => onChange('public')}
      />
    </div>
  );
}

function SourceToggle({ value, onChange }) {
  return (
    <div className="auction-choice-grid">
      {SOURCE_OPTIONS.map((opt) => (
        <ChoiceCard
          key={opt.value}
          icon={ArrowLeftRight}
          badge={opt.badge}
          title={opt.label}
          description={opt.description}
          active={value === opt.value}
          onClick={() => onChange(opt.value)}
        />
      ))}
    </div>
  );
}

function RoleSystemToggle({ value, onChange }) {
  return (
    <div className="auction-choice-grid">
      {ROLE_SYSTEM_OPTIONS.map((opt) => (
        <ChoiceCard
          key={opt.value}
          icon={Wand2}
          badge={opt.badge}
          title={opt.label}
          description={opt.description}
          active={value === opt.value}
          onClick={() => onChange(opt.value)}
        />
      ))}
    </div>
  );
}

const AUCTION_TYPE_OPTIONS = [
  {
    value: 'auction',
    icon: Gavel,
    label: 'Asta',
    description: 'Segui il Market e gli acquisti di tutte le squadre.',
    badge: 'Default',
  },
  {
    value: 'listone',
    icon: ListOrdered,
    label: 'Asta a listone',
    description: 'Componi solo la tua rosa da un listone e analizzala.',
    badge: 'Nuovo',
  },
];

function AuctionTypeToggle({ value, onChange }) {
  return (
    <div className="auction-choice-grid">
      {AUCTION_TYPE_OPTIONS.map((opt) => (
        <ChoiceCard
          key={opt.value}
          icon={opt.icon}
          badge={opt.badge}
          title={opt.label}
          description={opt.description}
          active={value === opt.value}
          onClick={() => onChange(opt.value)}
        />
      ))}
    </div>
  );
}

// Pannello statistiche asta: un unico riquadro "glass" con il totale in
// evidenza e la ripartizione come voce di legenda (icona colorata + valore),
// non più 5 card separate con bordo/hover che sembravano pulsanti cliccabili.
function AuctionStatsPanel({ stats }) {
  const breakdown = [
    { icon: Users, label: 'Classic', value: stats.classic, accent: 'blue' },
    { icon: Wand2, label: 'Mantra', value: stats.mantra, accent: 'orange' },
    { icon: Sparkles, label: 'FantApp', value: stats.fantapp, accent: 'teal' },
    { icon: Globe2, label: 'FantaMaster', value: stats.fantamaster, accent: 'green' },
  ];

  return (
    <div className="auction-stats-panel glass">
      <div className="auction-stats-panel__total">
        <div className="auction-stats-panel__totalIcon">
          <Layers size={20} />
        </div>
        <div>
          <strong>{stats.total}</strong>
          <span>Aste totali</span>
        </div>
      </div>

      <div className="auction-stats-panel__breakdown">
        {breakdown.map(({ icon: Icon, label, value, accent }) => (
          <div key={label} className={`auction-stats-panel__item auction-stats-panel__item--${accent}`}>
            <Icon size={14} />
            <strong>{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalysisUnavailableOverlay({ auction, onClose }) {
  if (!auction) return null;

  return (
    <div className="auction-modal-backdrop" onClick={onClose}>
      <div className="auction-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="auction-modal__head">
          <div>
            <div className="auction-modal__eyebrow auction-modal__eyebrow--error">
              <AlertTriangle size={14} />
              Analisi non disponibile
            </div>
            <h2>Nessun partecipante</h2>
            <p>
              L’asta <strong>{auction.name}</strong> non ha ancora partecipanti: l’analisi ha
              bisogno di almeno la tua squadra per calcolare qualcosa.
            </p>
          </div>

          <button className="auction-modal__close" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>

        <div className="auction-error-box">
          <span>
            Aggiungi almeno un partecipante alla lega (anche solo tu): potrai poi entrare
            nell’Analisi Asta.
          </span>
        </div>

        <div className="auction-form__actions">
          <button type="button" className="auction-cancel-btn" onClick={onClose}>
            Ho capito
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteAuctionOverlay({ auction, onClose, onConfirm, loading }) {
  if (!auction) return null;

  return (
    <div className="auction-modal-backdrop" onClick={onClose}>
      <div className="auction-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="auction-modal__head">
          <div>
            <div className="auction-modal__eyebrow">
              <AlertTriangle size={14} />
              Elimina asta
            </div>
            <h2>Confermi l’eliminazione?</h2>
            <p>
              Stai per cancellare <strong>{auction.name}</strong> e tutti i suoi acquisti collegati.
            </p>
          </div>

          <button className="auction-modal__close" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>

        <div className="auction-delete-box">
          <strong>Operazione irreversibile</strong>
          <span>L’asta sparirà dalla tua lista e verranno rimossi anche i giocatori collegati.</span>
        </div>

        <div className="auction-form__actions">
          <button type="button" className="auction-cancel-btn" onClick={onClose}>
            Annulla
          </button>

          <button type="button" className="auction-delete-confirm-btn" onClick={onConfirm} disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={16} className="spin" />
                Elimino...
              </>
            ) : (
              <>
                <AlertTriangle size={16} />
                Elimina asta
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function buildMissingFields({
  auctionName,
  participantsCount,
  credits,
  teamNames,
  myTeamIndex,
  roleSystem,
  roleLimits,
  mantraMovementLimit,
  isListone = false,
}) {
  const missing = [];

  if (!String(auctionName || '').trim()) missing.push('nome asta');
  if (Number(credits) <= 0) missing.push('crediti iniziali');

  if (isListone) {
    // Listone: conta solo la rosa personale → serve solo il nome della tua
    // squadra, niente numero partecipanti né nomi delle altre squadre.
    if (!String(teamNames?.[0] || '').trim()) missing.push('nome della tua squadra');
  } else {
    if (Number(participantsCount) < 1) missing.push('numero partecipanti');

    const teamCount = Math.max(Number(participantsCount) || 0, 0);
    const currentTeams = Array.from({ length: teamCount }, (_, idx) => String(teamNames?.[idx] || '').trim());

    currentTeams.forEach((name, idx) => {
      if (!name) missing.push(`nome squadra ${idx + 1}`);
    });

    if (!currentTeams[myTeamIndex]) {
      missing.push('la tua squadra');
    }
  }

  if (roleSystem === 'classic') {
    ['P', 'D', 'C', 'A'].forEach((role) => {
      if (Number(roleLimits?.[role]) <= 0) {
        missing.push(`limite ${ROLE_FULL_LABELS[role].toLowerCase()}`);
      }
    });
  } else {
    if (Number(roleLimits?.P) <= 0) {
      missing.push('limite portieri');
    }
    if (Number(mantraMovementLimit) <= 0) {
      missing.push('limite giocatori di movimento');
    }
  }

  return missing;
}

function buildRoleOverridesForAuction(playersSource, roleSystem) {
  const source = normalizeText(playersSource);

  if (roleSystem === 'mantra') {
    return normalizeRoleOverridesInput(DEFAULT_MANTRA_ROLE_CONFIGURATION);
  }

  if (source === 'fantapp' && roleSystem === 'classic') {
    return normalizeRoleOverridesInput(FANTAPP_ROLE_OVERRIDES_CONFIGURATION);
  }

  return {};
}

export default function AuctionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingDeleteAuction, setPendingDeleteAuction] = useState(null);
  const [analysisBlockedAuction, setAnalysisBlockedAuction] = useState(null);
  const [auctions, setAuctions] = useState([]);
  const [showPublicOverlay, setShowPublicOverlay] = useState(false);
  const [publicAuctions, setPublicAuctions] = useState([]);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [watchingId, setWatchingId] = useState(null);
  const [watchedIds, setWatchedIds] = useState(new Set());
  const [togglingId, setTogglingId] = useState(null);

  const [auctionName, setAuctionName] = useState('');
  const [participantsCount, setParticipantsCount] = useState(8);
  const [credits, setCredits] = useState(500);
  const [teamNames, setTeamNames] = useState(Array.from({ length: 1 }, () => ''));
  const [myTeamIndex, setMyTeamIndex] = useState(0);
  const [roleLimits, setRoleLimits] = useState(DEFAULT_ROSTER_LIMITS);
  // Minimo per ruolo: di default coincide col massimo (rosa da riempire
  // esattamente), ma può essere abbassato per lasciare margine di scelta.
  const [roleMinLimits, setRoleMinLimits] = useState(DEFAULT_ROSTER_LIMITS);
  const [mantraMovementLimit, setMantraMovementLimit] = useState(
    Number(DEFAULT_ROSTER_LIMITS.D || 0) +
    Number(DEFAULT_ROSTER_LIMITS.C || 0) +
    Number(DEFAULT_ROSTER_LIMITS.A || 0)
  );
  const [mantraMovementMinLimit, setMantraMovementMinLimit] = useState(
    Number(DEFAULT_ROSTER_LIMITS.D || 0) +
    Number(DEFAULT_ROSTER_LIMITS.C || 0) +
    Number(DEFAULT_ROSTER_LIMITS.A || 0)
  );

  const [auctionVisibility, setAuctionVisibility] = useState('private');
  const [playersSource, setPlayersSource] = useState('FantApp');
  const [roleSystem, setRoleSystem] = useState('classic');
  const [auctionType, setAuctionType] = useState('auction'); // 'auction' | 'listone'
  const isListone = auctionType === 'listone';

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formMessage, setFormMessage] = useState('');

  const PARTICIPANTS_MIN = 0;
  const PARTICIPANTS_MAX = 20;

  const loadAuctions = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const [ownList, watchedList] = await Promise.all([
        getUserAuctions(user.uid),
        getUserWatchedAuctions(user.uid).catch(() => []),
      ]);
      setAuctions([...ownList, ...watchedList]);
      setWatchedIds(new Set(watchedList.map((a) => a.id)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuctions().catch(() => setLoading(false));
  }, [user?.uid]);

  // Migrazione una tantum del PMA cross-asta al nuovo schema (rolesMode +
  // normalizzazione sui crediti, vedi auctionService.js): senza questo, le
  // aste esistenti restano "bloccate" sul vecchio schema finché non vengono
  // riaperte una per una, e il PMA sembrerebbe vuoto anche avendo dati
  // storici. Gira una sola volta per utente (flag in localStorage) — se va
  // in errore (offline, ecc.) il flag non viene salvato e riproverà al
  // prossimo caricamento.
  useEffect(() => {
    if (!user?.uid) return;
    const flagKey = `pmaBackfillDone_${user.uid}`;
    if (typeof window === 'undefined' || localStorage.getItem(flagKey)) return;
    backfillAllPmaStats(user.uid)
      .then(() => localStorage.setItem(flagKey, '1'))
      .catch(() => {});
  }, [user?.uid]);

  useEffect(() => {
    const count = Math.max(Number(participantsCount || 0), 1);
    setTeamNames((current) => Array.from({ length: count }, (_, idx) => current[idx] || ''));
    setMyTeamIndex((current) => Math.min(current, Math.max(count - 1, 0)));
  }, [participantsCount]);

  const filledTeams = useMemo(() => countFilledTeams(teamNames), [teamNames]);

  // Nomi squadra con bestemmie: bloccano la creazione dell'asta.
  const blasphemousTeams = useMemo(() => {
    const set = new Set();
    teamNames.forEach((name, idx) => {
      if (containsBlasphemy(name)) set.add(idx);
    });
    return set;
  }, [teamNames]);

  const dashboardStats = useMemo(() => {
    const list = Array.isArray(auctions) ? auctions : [];

    const countClassic = list.filter((a) => getAuctionRoleSystem(a) === 'classic').length;
    const countMantra = list.filter((a) => getAuctionRoleSystem(a) === 'mantra').length;
    const countFantApp = list.filter((a) => getAuctionSource(a) === 'fantapp').length;
    const countFantaMaster = list.filter((a) => getAuctionSource(a) === 'fantamaster').length;

    return {
      total: list.length,
      classic: countClassic,
      mantra: countMantra,
      fantapp: countFantApp,
      fantamaster: countFantaMaster,
    };
  }, [auctions]);

  const missingFields = useMemo(
    () =>
      buildMissingFields({
        auctionName,
        participantsCount,
        credits,
        teamNames,
        myTeamIndex,
        roleSystem,
        roleLimits,
        mantraMovementLimit,
        isListone,
      }),
    [auctionName, participantsCount, credits, teamNames, myTeamIndex, roleSystem, roleLimits, mantraMovementLimit, isListone]
  );

  const canCreate =
    missingFields.length === 0 &&
    blasphemousTeams.size === 0 &&
    (auctionVisibility === 'private' || auctionVisibility === 'public') &&
    (roleSystem === 'classic' || roleSystem === 'mantra') &&
    (playersSource === 'FantApp' || playersSource === 'FantaMaster');

  const handleTeamNameChange = (index, value) => {
    setTeamNames((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  // Alzare il massimo di un ruolo non tocca il minimo; abbassarlo sotto il
  // minimo corrente trascina giù anche quest'ultimo (min <= max sempre).
  const handleRoleMaxChange = (role, nextValue) => {
    const max = Math.max(0, Number(nextValue) || 0);
    setRoleLimits((current) => ({ ...current, [role]: max }));
    setRoleMinLimits((current) => ({ ...current, [role]: Math.min(Number(current[role]) || 0, max) }));
  };

  const handleRoleMinChange = (role, nextValue) => {
    const max = Number(roleLimits[role]) || 0;
    const min = Math.max(0, Math.min(Number(nextValue) || 0, max));
    setRoleMinLimits((current) => ({ ...current, [role]: min }));
  };

  const handleMantraMovementMaxChange = (nextValue) => {
    const max = Math.max(0, Number(nextValue) || 0);
    setMantraMovementLimit(max);
    setMantraMovementMinLimit((current) => Math.min(Number(current) || 0, max));
  };

  const handleMantraMovementMinChange = (nextValue) => {
    const max = Number(mantraMovementLimit) || 0;
    setMantraMovementMinLimit(Math.max(0, Math.min(Number(nextValue) || 0, max)));
  };

  const resetForm = () => {
    setAuctionName('');
    setParticipantsCount(1);
    setCredits(500);
    setTeamNames(Array.from({ length: 1 }, () => ''));
    setMyTeamIndex(0);
    setRoleLimits(DEFAULT_ROSTER_LIMITS);
    setRoleMinLimits(DEFAULT_ROSTER_LIMITS);
    setMantraMovementLimit(
      Number(DEFAULT_ROSTER_LIMITS.D || 0) +
      Number(DEFAULT_ROSTER_LIMITS.C || 0) +
      Number(DEFAULT_ROSTER_LIMITS.A || 0)
    );
    setMantraMovementMinLimit(
      Number(DEFAULT_ROSTER_LIMITS.D || 0) +
      Number(DEFAULT_ROSTER_LIMITS.C || 0) +
      Number(DEFAULT_ROSTER_LIMITS.A || 0)
    );
    setAuctionVisibility('private');
    setPlayersSource('FantApp');
    setRoleSystem('classic');
    setAuctionType('auction');
    setSubmitAttempted(false);
    setFormMessage('');
  };

  const openCreate = () => {
    resetForm();
    setShowCreate(true);
  };

  const closeCreate = () => {
    setShowCreate(false);
    setFormMessage('');
    setSubmitAttempted(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    if (!user?.uid) return;

    if (blasphemousTeams.size > 0) {
      const firstBad = Math.min(...blasphemousTeams);
      setFormMessage(
        `Il nome della squadra ${firstBad + 1} non è consentito: evita le bestemmie.`
      );
      return;
    }

    if (missingFields.length > 0 || !canCreate) {
      const firstMissing = missingFields[0];
      setFormMessage(
        firstMissing
          ? `Ti manca ancora ${firstMissing}.`
          : 'Controlla i campi richiesti prima di creare l’asta.'
      );
      return;
    }

    setCreating(true);
    try {
      const isMantra = roleSystem === 'mantra';
      const roleOverrides = buildRoleOverridesForAuction(playersSource, roleSystem);

      const mantraMovement = Number(mantraMovementLimit || 0);
      const mantraMovementMin = Math.min(Number(mantraMovementMinLimit || 0), mantraMovement);

      // Listone: nel form si compila solo il nome della propria squadra
      // (teamNames[0]); tutto il resto della "lega" non serve.
      const listoneTeamName = String(teamNames?.[0] || '').trim();
      const effectiveTeamNames = isListone ? [listoneTeamName] : teamNames.map((t) => t.trim());
      const effectiveMyTeamIndex = isListone ? 0 : Number(myTeamIndex);
      const effectiveParticipants = isListone ? 1 : Number(participantsCount);

      const auctionId = await createAuction(user.uid, {
        name: auctionName.trim(),
        participantsCount: effectiveParticipants,
        credits: Number(credits),
        teamNames: effectiveTeamNames,
        myTeamIndex: effectiveMyTeamIndex,
        myTeamName: effectiveTeamNames[effectiveMyTeamIndex],
        listone: isListone,
        rosterLimits: isMantra
          ? {
            P: Number(roleLimits.P || 0),
            M: mantraMovement,
          }
          : {
            P: Number(roleLimits.P),
            D: Number(roleLimits.D),
            C: Number(roleLimits.C),
            A: Number(roleLimits.A),
          },
        rosterMinLimits: isMantra
          ? {
            P: Math.min(Number(roleMinLimits.P || 0), Number(roleLimits.P || 0)),
            M: mantraMovementMin,
          }
          : {
            P: Math.min(Number(roleMinLimits.P || 0), Number(roleLimits.P || 0)),
            D: Math.min(Number(roleMinLimits.D || 0), Number(roleLimits.D || 0)),
            C: Math.min(Number(roleMinLimits.C || 0), Number(roleLimits.C || 0)),
            A: Math.min(Number(roleMinLimits.A || 0), Number(roleLimits.A || 0)),
          },
        mantraMovementLimit: isMantra ? mantraMovement : null,
        mantraMovementMinLimit: isMantra ? mantraMovementMin : null,
        visibility: auctionVisibility,
        playersSource,
        rolesMode: roleSystem,
        roleOverrides,
      });

      await loadAuctions();
      setShowCreate(false);
      navigate(`/aste/${auctionId}`);
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!user?.uid || !pendingDeleteAuction?.id) return;

    setDeleting(true);
    try {
      await deleteAuction(user.uid, pendingDeleteAuction.id);
      setPendingDeleteAuction(null);
      await loadAuctions();
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleVisibility = async (auction) => {
    if (!user?.uid || !auction?.id) return;
    setTogglingId(auction.id);
    try {
      const next = auction.visibility === 'public' ? 'private' : 'public';
      await updateAuction(user.uid, auction.id, { visibility: next });
      if (next === 'public') {
        await syncPublicAuction(user.uid, auction.id, { ...auction, visibility: 'public' });
      } else {
        await removePublicAuction(user.uid, auction.id);
      }
      await loadAuctions();
    } catch (err) {
      console.error('Errore toggle visibilità:', err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleOpenPublicOverlay = async () => {
    setShowPublicOverlay(true);
    setLoadingPublic(true);
    try {
      const list = await getPublicAuctions();
      // Escludi le proprie aste
      setPublicAuctions(list.filter((a) => a.ownerUid !== user?.uid));
    } catch {
      setPublicAuctions([]);
    } finally {
      setLoadingPublic(false);
    }
  };

  const handleToggleWatch = async (auction) => {
    if (!user?.uid) return;
    const alreadyWatched = watchedIds.has(auction.id);
    setWatchingId(auction.id);
    try {
      if (alreadyWatched) {
        await removeWatchedAuction(user.uid, auction.id);
        setWatchedIds((prev) => { const next = new Set(prev); next.delete(auction.id); return next; });
      } else {
        await addWatchedAuction(user.uid, auction.ownerUid, auction.id);
        setWatchedIds((prev) => new Set(prev).add(auction.id));
      }
      await loadAuctions();
    } catch (err) {
      console.error('Errore toggle watch:', err);
    } finally {
      setWatchingId(null);
    }
  };

  const handleUnwatch = async (auction) => {
    if (!user?.uid) return;
    setWatchingId(auction.id);
    try {
      await removeWatchedAuction(user.uid, auction.id);
      await loadAuctions();
    } catch (err) {
      console.error('Errore unwatch:', err);
    } finally {
      setWatchingId(null);
    }
  };

  return (
    <main className="container auctions-page">
      <TopBar />

      <section className="auctions-hero glass">
        <div className="auctions-hero__copy">
          <div className="auctions-hero__eyebrow">
            <Sparkles size={16} />
            Gestione aste
          </div>

          <h1>Le tue aste</h1>

          <p>
            Qui trovi tutte le aste collegate al tuo account. Puoi aprire un'asta già esistente oppure crearne una nuova.
          </p>
        </div>

        <div className="auctions-hero__aside">
          <AuctionStatsPanel stats={dashboardStats} />

          <div className="auctions-hero__actions">
            <button
              className="create-auction-btn create-auction-btn--secondary"
              onClick={() => navigate('/strategie')}
              type="button"
            >
              <NotebookPen size={18} />
              Strategie
            </button>

            <button
              className="create-auction-btn create-auction-btn--secondary"
              onClick={handleOpenPublicOverlay}
              type="button"
            >
              <HatGlasses size={18} />
              Aste pubbliche
            </button>

            <button
              className="create-auction-btn create-auction-btn--hero"
              onClick={openCreate}
              type="button"
            >
              <Plus size={18} />
              Nuova asta
            </button>
          </div>
        </div>
      </section>

      <section className="auctions-list-section">
        {loading ? (
          <div className="empty-box glass">Caricamento aste...</div>
        ) : auctions.length ? (
          <div className="auctions-list-grid">
            {auctions.map((auction) => (
              <AuctionCard
                key={auction.id}
                auction={auction}
                boughtPlayersCount={Number(auction.playersBoughtCount || 0)}
                isWatched={!!auction.isWatched}
                onOpen={(id) =>
                  auction.isWatched
                    ? navigate(`/aste/${id}?ownerUid=${auction.watchedOwnerUid}`)
                    : navigate(`/aste/${id}`)
                }
                onOpenAnalysis={(id) => {
                  const teamsCount = auction.teamNames?.length || auction.participantsCount || 0;
                  if (teamsCount < 1) {
                    setAnalysisBlockedAuction(auction);
                    return;
                  }
                  if (auction.isWatched) {
                    navigate(`/aste/${id}/analisi?ownerUid=${auction.watchedOwnerUid}`);
                  } else {
                    navigate(`/aste/${id}/analisi`);
                  }
                }}
                onDelete={
                  auction.isWatched
                    ? () => handleUnwatch(auction)
                    : () => setPendingDeleteAuction(auction)
                }
                deleting={watchingId === auction.id}
                onToggleVisibility={auction.isWatched ? undefined : () => handleToggleVisibility(auction)}
                toggling={togglingId === auction.id}
              />
            ))}
          </div>
        ) : (
          <div className="empty-box glass">
            Non hai ancora creato nessuna asta. Premi “Nuova asta” per iniziare.
          </div>
        )}
      </section>

      <footer className="auctions-footer">
        <span>FantApp! - All rights reserved - Sorgenti giocatori </span>
        <a href="https://www.fantamaster.it/" target="_blank" rel="noreferrer">
          FantaMaster
        </a>
      </footer>

      {showCreate && (
        <div className="auction-modal-backdrop" onClick={closeCreate}>
          <div className="auction-modal glass" onClick={(e) => e.stopPropagation()}>
            <div className="auction-modal__head">
              <div>
                <div className="auction-modal__eyebrow">
                  <AlertTriangle size={14} />
                  Crea nuova asta
                </div>
                <h2>Configura la tua lega</h2>
                <p>Definisci nome, partecipanti, crediti, sorgente giocatori, limiti rosa, visibilità e sistema ruoli.</p>
              </div>

              <button className="auction-modal__close" onClick={closeCreate} type="button">
                <X size={18} />
              </button>
            </div>

            <form className="auction-form" onSubmit={handleCreate}>
              <div className="auction-form__grid">
                <label>
                  <span>Nome asta</span>
                  <input
                    value={auctionName}
                    onChange={(e) => setAuctionName(e.target.value)}
                    placeholder="Es. Lega Marco 2026"
                    required
                  />
                </label>

                {!isListone && (
                  <label>
                    <span>Numero partecipanti</span>

                    <div className="auction-number-field">
                      <input
                        className="auction-number-input"
                        type="number"
                        min={PARTICIPANTS_MIN}
                        max={PARTICIPANTS_MAX}
                        step="1"
                        value={participantsCount}
                        onChange={(e) =>
                          setParticipantsCount(
                            Math.min(
                              PARTICIPANTS_MAX,
                              Math.max(PARTICIPANTS_MIN, Number(e.target.value || PARTICIPANTS_MIN))
                            )
                          )
                        }
                        required
                      />

                      {Number(participantsCount) === 1 && (
                        <Tooltip
                          as="span"
                          className="auction-number-field__info"
                          content="Impostando un solo partecipante potrai gestire solamente la tua rosa"
                          aria-label="Con 1 partecipante potrai inserire solo i tuoi giocatori"
                        >
                          <Info size={14} />
                        </Tooltip>
                      )}
                    </div>
                  </label>
                )}

                {isListone && (
                  <label>
                    <span>Nome della tua squadra</span>
                    <input
                      value={teamNames[0] || ''}
                      onChange={(e) => handleTeamNameChange(0, e.target.value)}
                      placeholder="Es. Real Marco"
                      required
                    />
                  </label>
                )}

                <label>
                  <span>Crediti iniziali</span>
                  <input
                    className="auction-number-input"
                    type="number"
                    min="1"
                    step="1"
                    value={credits}
                    onChange={(e) => setCredits(Number(e.target.value))}
                    required
                  />
                </label>
              </div>

              <div className="auction-form__block auction-form__block--plain">
                <div className="auction-form__teamsHead">
                  <h3>Sorgente giocatori</h3>
                  <span>FantApp! è la scelta predefinita</span>
                </div>

                <SourceToggle value={playersSource} onChange={setPlayersSource} />
              </div>

              <div className="auction-form__block auction-form__block--plain">
                <div className="auction-form__teamsHead">
                  <h3>Visibilità asta</h3>
                  <span>Le aste pubbliche sono visibili a tutti gli utenti</span>
                </div>

                <VisibilityToggle value={auctionVisibility} onChange={setAuctionVisibility} />
              </div>

              <div className="auction-form__block auction-form__block--plain">
                <div className="auction-form__teamsHead">
                  <h3>Modalità ruoli</h3>
                  <span>Tipologia dei ruoli</span>
                </div>

                <RoleSystemToggle value={roleSystem} onChange={setRoleSystem} />
              </div>

              <div className="auction-form__block auction-form__block--plain">
                <div className="auction-form__teamsHead">
                  <h3>Tipo asta</h3>
                  <span>Nel listone gestisci solo la tua rosa</span>
                </div>

                <AuctionTypeToggle value={auctionType} onChange={setAuctionType} />
              </div>

              <div className="auction-form__block auction-form__block--plain">
                <div className="auction-form__teamsHead">
                  <h3>Limiti rosa</h3>
                  <span>{roleSystem === 'classic' ? 'Consiglio: 3P, 8D, 8C, 6A' : 'Portieri e giocatori di movimento'}</span>
                </div>

                {roleSystem === 'classic' ? (
                  <div className="auction-form__limitsGrid">
                    {['P', 'D', 'C', 'A'].map((role) => (
                      <RoleLimitsCard
                        key={role}
                        role={role}
                        minValue={roleMinLimits[role]}
                        maxValue={roleLimits[role]}
                        onMinChange={(nextValue) => handleRoleMinChange(role, nextValue)}
                        onMaxChange={(nextValue) => handleRoleMaxChange(role, nextValue)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="auction-form__limitsGrid auction-form__limitsGrid--dual">
                    <RoleLimitsCard
                      role="P"
                      minValue={roleMinLimits.P}
                      maxValue={roleLimits.P}
                      onMinChange={(nextValue) => handleRoleMinChange('P', nextValue)}
                      onMaxChange={(nextValue) => handleRoleMaxChange('P', nextValue)}
                    />

                    <RoleLimitsCard
                      role="M"
                      minValue={mantraMovementMinLimit}
                      maxValue={mantraMovementLimit}
                      onMinChange={handleMantraMovementMinChange}
                      onMaxChange={handleMantraMovementMaxChange}
                    />
                  </div>
                )}
              </div>

              {!isListone && (
              <div className="auction-form__teams">
                <div className="auction-form__teamsHead auction-form__teamsHead--stacked">
                  <div>
                    <h3>Squadre</h3>
                    <span>Seleziona la tua squadra</span>
                  </div>

                  <div className="auction-form__teamsMeta">
                    <span>Squadre compilate</span>
                    <strong>
                      {filledTeams}/{participantsCount}
                    </strong>
                  </div>
                </div>

                <div className="auction-form__teamsList">
                  {teamNames.map((name, index) => {
                    const isBlasphemous = blasphemousTeams.has(index);
                    return (
                    <div key={index} className="auction-team-row">
                      <label className={`auction-team-row__input ${isBlasphemous ? 'is-invalid' : ''}`}>
                        <span>Squadra {index + 1}</span>
                        <input
                          value={name}
                          onChange={(e) => handleTeamNameChange(index, e.target.value)}
                          placeholder={`Nome squadra ${index + 1}`}
                          aria-invalid={isBlasphemous}
                          required
                        />
                        {isBlasphemous && (
                          <small className="auction-team-row__error">
                            Nome non consentito: niente bestemmie.
                          </small>
                        )}
                      </label>

                      <button
                        type="button"
                        className={`auction-team-row__mine ${myTeamIndex === index ? 'is-active' : ''}`}
                        onClick={() => setMyTeamIndex(index)}
                      >
                        La mia
                      </button>
                    </div>
                    );
                  })}
                </div>
              </div>
              )}

              {(submitAttempted && missingFields.length > 0) || formMessage ? (
                <div className="auction-form__notice auction-form__notice--warning">
                  <AlertTriangle size={14} />
                  <span>{formMessage || `Ti manca ancora ${missingFields[0] || 'un campo obbligatorio'}.`}</span>
                </div>
              ) : (
                <div className="auction-form__notice auction-form__notice--soft">
                  Compila i campi richiesti per creare l’asta.
                </div>
              )}

              <div className="auction-form__actions">
                <button type="button" className="auction-cancel-btn" onClick={closeCreate}>
                  Annulla
                </button>

                <button className="auction-create-btn" type="submit" disabled={creating}>
                  {creating ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      Creo asta...
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Crea asta
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {analysisBlockedAuction && (
        <AnalysisUnavailableOverlay
          auction={analysisBlockedAuction}
          onClose={() => setAnalysisBlockedAuction(null)}
        />
      )}

      {pendingDeleteAuction && (
        <DeleteAuctionOverlay
          auction={pendingDeleteAuction}
          onClose={() => setPendingDeleteAuction(null)}
          onConfirm={handleConfirmDelete}
          loading={deleting}
        />
      )}

      {showPublicOverlay && (
        <PublicAuctionsOverlay
          auctions={publicAuctions}
          loading={loadingPublic}
          watchedIds={watchedIds}
          watchingId={watchingId}
          onToggleWatch={handleToggleWatch}
          onClose={() => setShowPublicOverlay(false)}
        />
      )}
    </main>
  );
}

function PublicAuctionsOverlay({ auctions, loading, watchedIds, watchingId, onToggleWatch, onClose }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return auctions;
    return auctions.filter((a) =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.myTeamName || '').toLowerCase().includes(q)
    );
  }, [auctions, search]);

  return (
    <div className="auction-modal-backdrop auction-modal-backdrop--centered" onClick={onClose}>
      <div className="auction-modal auction-modal--wide auction-modal--centered glass" onClick={(e) => e.stopPropagation()}>
        <div className="auction-modal__head">
          <div>
            <div className="auction-modal__eyebrow">
              <HatGlasses size={14} />
              Aste pubbliche
            </div>
            <h2>Cerca aste pubbliche</h2>
            <p>Aggiungi un'asta pubblica alla tua lista per seguirla in sola lettura.</p>
          </div>
          <button className="auction-modal__close" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>

        <div className="public-overlay__search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Cerca per nome asta o squadra..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        {loading ? (
          <div className="public-overlay__loading">
            <Loader2 size={20} className="spin" />
            Caricamento aste pubbliche...
          </div>
        ) : filtered.length === 0 ? (
          <div className="public-overlay__empty">
            {search ? 'Nessuna asta corrisponde alla ricerca.' : 'Nessuna asta pubblica disponibile.'}
          </div>
        ) : (
          <div className="public-overlay__grid">
            {filtered.map((auction) => {
              const mode = getAuctionRolesMode(auction);
              const limits = {
                ...normalizeRosterLimits(auction.rosterLimits || DEFAULT_ROSTER_LIMITS),
                M: getAuctionMantraMovementLimit(auction),
              };
              const isWatched = watchedIds.has(auction.id);
              const isBusy = watchingId === auction.id;
              return (
                <div key={auction.id} className="public-auction-card glass">
                  <div className="public-auction-card__head">
                    <div>
                      <div className="public-auction-card__eyebrow">
                        <HatGlasses size={12} />
                        Pubblica
                      </div>
                      <div className="public-auction-card__name-row">
                        <strong>{auction.name}</strong>
                        {auction.myTeamName && (
                          <>
                            <span className="public-auction-card__divider" />
                            <span className="public-auction-card__owner">Host: {auction.myTeamName}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="public-auction-card__stats">
                    <Tooltip as="span" content="Numero di squadre partecipanti"><Users size={13} /> {auction.participantsCount} squadre</Tooltip>
                    <Tooltip as="span" content="Crediti iniziali per squadra"><Coins size={13} /> {auction.credits} cr</Tooltip>
                    <Tooltip as="span" content="Modalità ruoli">{mode === 'mantra' ? 'Mantra' : 'Classic'}</Tooltip>
                    <Tooltip as="span" content="Sorgente giocatori">{auction.playersSource === 'FantApp' ? 'FantApp!' : 'FantaMaster'}</Tooltip>
                  </div>
                  <div className="public-auction-card__limits">
                    <Tooltip as="span" content={`Portieri: max ${limits.P}`} className="public-auction-card__role-chip public-auction-card__role-chip--p">P {limits.P}</Tooltip>
                    {mode === 'classic' ? (
                      <>
                        <Tooltip as="span" content={`Difensori: max ${limits.D}`} className="public-auction-card__role-chip public-auction-card__role-chip--d">D {limits.D}</Tooltip>
                        <Tooltip as="span" content={`Centrocampisti: max ${limits.C}`} className="public-auction-card__role-chip public-auction-card__role-chip--c">C {limits.C}</Tooltip>
                        <Tooltip as="span" content={`Attaccanti: max ${limits.A}`} className="public-auction-card__role-chip public-auction-card__role-chip--a">A {limits.A}</Tooltip>
                      </>
                    ) : (
                      <Tooltip as="span" content={`Giocatori di movimento: max ${limits.M}`} className="public-auction-card__role-chip public-auction-card__role-chip--m">M {limits.M}</Tooltip>
                    )}
                  </div>
                  <button
                    className={`public-auction-card__btn ${isWatched ? 'public-auction-card__btn--remove' : ''}`}
                    type="button"
                    disabled={isBusy}
                    onClick={() => onToggleWatch(auction)}
                  >
                    {isBusy ? (
                      <Loader2 size={14} className="spin" />
                    ) : isWatched ? (
                      <><EyeOff size={14} /> Smetti di seguire</>
                    ) : (
                      <><Eye size={14} /> Segui</>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}